import { Bot, Settings, User, UserCheck } from "lucide-react";
import {
  ACTIVITY_EVENT_ACTOR_TYPE,
  OPPORTUNITY_STATUS_DISPLAY_NAMES,
  QUOTE_STATUS_DISPLAY_NAMES,
  TASK_METADATA_ENTITY_TYPE,
  formatCurrency,
  formatDateOnly,
  formatQuoteNumber,
  isReceiveEmailTrigger,
  isReceiveSmsTrigger,
} from "@forgepoint/shared";
import type {
  EmailContent,
  LineItemChange,
  OpportunityActivityEvent,
  OpportunityCreatedMetadata,
  OpportunityUpdatedMetadata,
  PublicUser,
  QuoteUpdatedMetadata,
  SmsContent,
} from "@forgepoint/shared";
import {
  memberPerson,
  type ItemHistoryActor,
  type ItemHistoryPerson,
} from "@/modules/items/itemHistoryLabels";
import type {
  ItemHistoryDetail,
  ItemHistoryEntryLine,
} from "@/modules/items/itemHistoryRows";

/** One changed field, as the item history detail lines render it. */
export type ActivityChangeLine = Extract<
  ItemHistoryEntryLine,
  { type: "changed" }
>;

/** One piece of an activity sentence, in reading order. */
export type ActivitySentencePart =
  | { type: "verb"; text: string }
  | { type: "object"; text: string }
  | { type: "person"; text: string }
  | { type: "quote"; quoteId: string | null; label: string }
  | { type: "chip"; label: string; details: ItemHistoryDetail[] }
  | { type: "download"; assetId: string; label: string }
  | { type: "count"; count: number };

/** An event as one sentence, with its inline change, context, and message preview. */
export interface ActivitySentence {
  parts: ActivitySentencePart[];
  change: ActivityChangeLine | null;
  context: string | null;
  preview: string | null;
}

/** The full message an event carries, for its detail popover. */
export type ActivityMessage =
  | { type: "email"; content: EmailContent }
  | { type: "sms"; content: SmsContent }
  | {
      type: "message";
      message: string;
      messageType: string;
      messageLabel: string | null;
    };

/** The names ids resolve through, gathered by the caller. */
export interface ActivityNames {
  agentName: string;
  stageNames: ReadonlyMap<string, string>;
  userNames: ReadonlyMap<string, string>;
  companyNames: ReadonlyMap<string, string>;
  personNames: ReadonlyMap<string, string>;
  locationNames: ReadonlyMap<string, string>;
  quoteDisplayNumbers: ReadonlyMap<string, string>;
  quoteNumberPadWidth: number | null;
  quoteNumberPrefix: string | null;
}

/** The ids an activity log mentions whose names may need fetching. */
export interface ActivityReferencedIds {
  companyIds: string[];
  personIds: string[];
  locationIds: string[];
}

// Old and new values longer than this together read from a chip instead.
const INLINE_CHANGE_MAX_CHARS = 40;
const NONE = "None";

/**
 * Turns an opportunity event into the sentence its activity line renders.
 * @param event - The event being rendered
 * @param names - The names ids resolve through
 * @returns The sentence
 */
export function buildActivitySentence(
  event: OpportunityActivityEvent,
  names: ActivityNames,
): ActivitySentence {
  switch (event.eventType) {
    case "OPPORTUNITY_CREATED":
      return sentence([
        verb("created the opportunity ·"),
        createdChip(event.eventMetadata, names),
      ]);
    case "OPPORTUNITY_UPDATED":
      return opportunityUpdatedSentence(event.eventMetadata, names);
    case "OPPORTUNITY_ARCHIVED":
      return sentence([verb("archived the opportunity")]);
    case "OPPORTUNITY_UNARCHIVED":
      return sentence([verb("restored the opportunity")]);
    case "AGENT_USER_MESSAGE":
    case "AGENT_ACTION_EXECUTED":
    case "AGENT_TRIGGER_EXECUTED":
      return messageSentence(event, names);
    case "QUOTE_UPDATED":
      return quoteUpdatedSentence(event.eventMetadata, names);
    case "QUOTE_REVISION_CREATED":
      return revisionSentence(event.eventMetadata, names);
    case "QUOTE_VIEWED":
      return {
        ...sentence([verb("viewed"), quotePart(event.eventMetadata, names)]),
        context: event.eventMetadata.viewerLocation ?? null,
      };
    case "QUOTE_PDF_GENERATED":
      return sentence([
        verb("generated a"),
        {
          type: "download",
          assetId: event.eventMetadata.assetId,
          label: "PDF",
        },
        verb("for"),
        quotePart(event.eventMetadata, names),
      ]);
    case "QUOTE_VALIDATED":
      return validatedSentence(event.eventMetadata, names);
    case "QUOTE_DELETED":
      return sentence([
        verb("deleted"),
        {
          type: "quote",
          quoteId: null,
          label: quoteLabel(event.eventMetadata.quoteNumber, names),
        },
      ]);
    case "QUOTE_ADDED_TO_OPPORTUNITY":
    case "QUOTE_REMOVED_FROM_OPPORTUNITY":
    case "QUOTE_FINALIZED":
    case "QUOTE_SIGNED":
    case "QUOTE_ARCHIVED":
    case "QUOTE_UNARCHIVED":
    case "QUOTE_PUBLIC_ACCESS_REVOKED":
    case "QUOTE_PUBLIC_ACCESS_RESTORED":
      return sentence([
        verb(QUOTE_EVENT_VERBS[event.eventType]),
        quotePart(event.eventMetadata, names),
      ]);
    case "TASK_PENDING_APPROVAL": {
      const { actionName, approverNames, entityId, entityType } =
        event.eventMetadata;
      return sentence([
        verb("requested approval for"),
        object(actionName),
        ...taskQuoteParts(entityId, entityType, names),
        verb("from"),
        object(approverNames.join(", ")),
      ]);
    }
    case "TASK_APPROVED":
      return taskSentence("approved", event.eventMetadata, names);
    case "TASK_CANCELLED":
      return taskSentence("cancelled", event.eventMetadata, names);
  }
}

/**
 * Who an activity line names: the member, the standard agent with the user it
 * acted for, the system, or the customer (the signer, for a signature).
 * @param event - The event being rendered
 * @param agentName - The standard agent's display name
 * @param members - Org members by user id
 * @returns The actor
 */
export function activityActor(
  event: OpportunityActivityEvent,
  agentName: string,
  members: ReadonlyMap<string, PublicUser>,
): ItemHistoryActor {
  if (event.eventType === "TASK_APPROVED") {
    const { approverUserId, approverName } = event.eventMetadata;
    return userActor(memberPerson(approverUserId, approverName, members));
  }
  if (event.eventType === "TASK_CANCELLED") {
    const { cancellerUserId, cancellerName } = event.eventMetadata;
    return userActor(memberPerson(cancellerUserId, cancellerName, members));
  }
  if (event.eventType === "QUOTE_SIGNED") {
    return {
      ...faceless(event.eventMetadata.signerName, UserCheck),
      tone: "external",
    };
  }
  if (event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.SYSTEM) {
    return {
      ...faceless("System", Settings),
      tone: "machine",
    };
  }
  if (event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.EXTERNAL) {
    return {
      ...faceless(event.actorName ?? "Customer", UserCheck),
      tone: "external",
    };
  }
  if (event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.USER) {
    return userActor(memberPerson(event.actorId, event.actorName, members));
  }
  return {
    ...faceless(agentName, Bot),
    tone: "agent",
    behalf: event.userId ? memberPerson(event.userId, null, members) : null,
  };
}

/**
 * The full message an event carries, when it carries one.
 * @param event - The event being rendered
 * @returns The email, text, or chat message; null for other events
 */
export function activityMessage(
  event: OpportunityActivityEvent,
): ActivityMessage | null {
  if (event.eventType === "AGENT_USER_MESSAGE") {
    const { message, messageType, messageLabel, emailContent, smsContent } =
      event.eventMetadata;
    if (emailContent) {
      return {
        type: "email",
        content: { ...emailContent, body: emailContent.body || message },
      };
    }
    if (smsContent) {
      return {
        type: "sms",
        content: { ...smsContent, body: smsContent.body || message },
      };
    }
    return {
      type: "message",
      message,
      messageType,
      messageLabel: messageLabel ?? null,
    };
  }
  if (
    event.eventType === "AGENT_ACTION_EXECUTED" ||
    event.eventType === "AGENT_TRIGGER_EXECUTED"
  ) {
    const { emailContent, smsContent } = event.eventMetadata;
    if (emailContent) {
      return { type: "email", content: emailContent };
    }
    if (smsContent) {
      return { type: "sms", content: smsContent };
    }
  }
  return null;
}

/**
 * Adds a quote view count after the sentence when the line folds repeats.
 * @param base - The line's sentence
 * @param viewCount - How many views the line stands for
 * @returns The sentence, with a count part when there is more than one view
 */
export function withViewCount(
  base: ActivitySentence,
  viewCount: number,
): ActivitySentence {
  if (viewCount < 2) {
    return base;
  }
  return {
    ...base,
    parts: [...base.parts, { type: "count", count: viewCount }],
  };
}

/**
 * The company, person, and location ids the events mention, so the log can
 * fetch the names it does not hold.
 * @param events - The events being rendered
 * @returns The referenced ids, each deduped
 */
export function referencedActivityIds(
  events: readonly OpportunityActivityEvent[],
): ActivityReferencedIds {
  const companyIds = new Set<string>();
  const personIds = new Set<string>();
  const locationIds = new Set<string>();
  for (const event of events) {
    if (
      event.eventType !== "OPPORTUNITY_UPDATED" &&
      event.eventType !== "QUOTE_UPDATED"
    ) {
      continue;
    }
    const metadata = event.eventMetadata;
    companyIdsOf(event).forEach((id) => companyIds.add(id));
    [
      ...(metadata.oldPersonIds ?? []),
      ...(metadata.newPersonIds ?? []),
    ].forEach((id) => personIds.add(id));
    [metadata.oldLocationId, metadata.newLocationId]
      .filter((id): id is string => Boolean(id))
      .forEach((id) => locationIds.add(id));
  }
  return {
    companyIds: [...companyIds],
    personIds: [...personIds],
    locationIds: [...locationIds],
  };
}

/**
 * The fields an opportunity write changed, with ids resolved to names. A stage
 * change carries the status it implies, so the status line is left out then.
 * @param metadata - The update's recorded old and new values
 * @param names - The names ids resolve through
 * @returns One changed line per field
 */
function opportunityChangeLines(
  metadata: OpportunityUpdatedMetadata,
  names: ActivityNames,
): ActivityChangeLine[] {
  const m = metadata;
  const stageChanged = m.newStageId !== undefined;
  const lines: ActivityChangeLine[] = [];
  if (stageChanged) {
    lines.push(
      changedLine(
        "stage",
        "Stage",
        nameOf(names.stageNames, m.oldStageId, "Unknown stage"),
        nameOf(names.stageNames, m.newStageId, "Unknown stage"),
      ),
    );
  }
  if (m.oldStatus && m.newStatus && !stageChanged) {
    lines.push(
      changedLine(
        "status",
        "Status",
        OPPORTUNITY_STATUS_DISPLAY_NAMES[m.oldStatus],
        OPPORTUNITY_STATUS_DISPLAY_NAMES[m.newStatus],
      ),
    );
  }
  if (m.oldName !== undefined && m.newName !== undefined) {
    lines.push(changedLine("name", "Name", m.oldName, m.newName));
  }
  if (m.oldAmount !== undefined && m.newAmount !== undefined) {
    lines.push(
      changedLine(
        "amount",
        "Amount",
        formatCurrency(m.oldAmount),
        formatCurrency(m.newAmount),
      ),
    );
  }
  if (m.oldProbability !== undefined && m.newProbability !== undefined) {
    lines.push(
      changedLine(
        "probability",
        "Probability",
        `${m.oldProbability}%`,
        `${m.newProbability}%`,
      ),
    );
  }
  if (m.newCompanyId !== undefined) {
    lines.push(
      changedLine(
        "company",
        "Company",
        nameOf(names.companyNames, m.oldCompanyId, "Unknown company"),
        nameOf(names.companyNames, m.newCompanyId, "Unknown company"),
      ),
    );
  }
  if (m.newSalesRepUserId !== undefined) {
    lines.push(salesRepLine(m.oldSalesRepUserId, m.newSalesRepUserId, names));
  }
  if (m.newEstimatorUserIds !== undefined) {
    lines.push(
      changedLine(
        "estimators",
        "Estimators",
        namesOf(names.userNames, m.oldEstimatorUserIds, "Unknown user"),
        namesOf(names.userNames, m.newEstimatorUserIds, "Unknown user"),
      ),
    );
  }
  if (m.newPersonIds !== undefined) {
    lines.push(peopleLine(m.oldPersonIds, m.newPersonIds, names));
  }
  if (m.newLocationId !== undefined) {
    lines.push(locationLine(m.oldLocationId, m.newLocationId, names));
  }
  return lines;
}

/**
 * Everything a quote write changed: its fields, flagged edits, and line items.
 * @param metadata - The update's recorded changes
 * @param names - The names ids resolve through
 * @returns The detail lines, fields first
 */
function quoteChangeDetails(
  metadata: QuoteUpdatedMetadata,
  names: ActivityNames,
): ItemHistoryDetail[] {
  return [
    ...quoteFieldLines(metadata, names),
    ...quoteFlagLines(metadata),
    ...(metadata.lineItemChanges ?? []).flatMap(lineItemDetails),
  ];
}

function companyIdsOf(
  event: Extract<
    OpportunityActivityEvent,
    { eventType: "OPPORTUNITY_UPDATED" | "QUOTE_UPDATED" }
  >,
): string[] {
  const ids =
    event.eventType === "OPPORTUNITY_UPDATED"
      ? [event.eventMetadata.oldCompanyId, event.eventMetadata.newCompanyId]
      : [
          event.eventMetadata.oldCustomerCompanyId,
          event.eventMetadata.newCustomerCompanyId,
        ];
  return ids.filter((id): id is string => Boolean(id));
}

const QUOTE_EVENT_VERBS = {
  QUOTE_ADDED_TO_OPPORTUNITY: "added",
  QUOTE_REMOVED_FROM_OPPORTUNITY: "removed",
  QUOTE_FINALIZED: "finalized",
  QUOTE_SIGNED: "signed",
  QUOTE_ARCHIVED: "archived",
  QUOTE_UNARCHIVED: "restored",
  QUOTE_PUBLIC_ACCESS_REVOKED: "revoked public access to",
  QUOTE_PUBLIC_ACCESS_RESTORED: "restored public access to",
} as const;

function opportunityUpdatedSentence(
  metadata: OpportunityUpdatedMetadata,
  names: ActivityNames,
): ActivitySentence {
  const lines = opportunityChangeLines(metadata, names);
  if (lines.length === 0) {
    return sentence([verb("updated the opportunity")]);
  }
  if (lines.length > 1) {
    return sentence([verb("changed"), chip(`${lines.length} fields`, lines)]);
  }
  const [line] = lines;
  return singleChange(line.key === "stage" ? "moved" : "changed", line, []);
}

function quoteUpdatedSentence(
  metadata: QuoteUpdatedMetadata,
  names: ActivityNames,
): ActivitySentence {
  const quote = quotePart(metadata, names);
  const details = quoteChangeDetails(metadata, names);
  const fields = quoteFieldLines(metadata, names);
  if (details.length === 0) {
    return sentence([verb("updated"), quote]);
  }
  if (details.length === 1 && fields.length === 1) {
    return singleChange("changed", fields[0], [verb("on"), quote]);
  }
  return sentence([
    verb("updated"),
    quote,
    verb("·"),
    chip(plural(details.length, "change"), details),
  ]);
}

function revisionSentence(
  metadata: { newQuoteId: string; newRevisionNumber: number },
  names: ActivityNames,
): ActivitySentence {
  const revision = object(`Rev ${metadata.newRevisionNumber}`);
  const displayNumber = names.quoteDisplayNumbers.get(metadata.newQuoteId);
  if (!displayNumber) {
    return sentence([verb("created"), revision]);
  }
  return sentence([
    verb("created"),
    revision,
    verb("of"),
    {
      type: "quote",
      quoteId: metadata.newQuoteId,
      label: `Quote #${displayNumber}`,
    },
  ]);
}

function validatedSentence(
  metadata: {
    quoteId: string;
    quoteNumber: number;
    isValid: boolean;
    validationErrors?: string[];
  },
  names: ActivityNames,
): ActivitySentence {
  const base = sentence([verb("validated"), quotePart(metadata, names)]);
  if (metadata.isValid) {
    return base;
  }
  const errorCount = metadata.validationErrors?.length ?? 0;
  return { ...base, context: plural(errorCount, "error") };
}

function messageSentence(
  event: Extract<
    OpportunityActivityEvent,
    {
      eventType:
        | "AGENT_USER_MESSAGE"
        | "AGENT_ACTION_EXECUTED"
        | "AGENT_TRIGGER_EXECUTED";
    }
  >,
  names: ActivityNames,
): ActivitySentence {
  const message = activityMessage(event);
  const preview = message ? previewOf(message) : null;
  return { ...sentence(messageParts(event, message, names)), preview };
}

function messageParts(
  event: Extract<
    OpportunityActivityEvent,
    {
      eventType:
        | "AGENT_USER_MESSAGE"
        | "AGENT_ACTION_EXECUTED"
        | "AGENT_TRIGGER_EXECUTED";
    }
  >,
  message: ActivityMessage | null,
  names: ActivityNames,
): ActivitySentencePart[] {
  if (event.eventType === "AGENT_ACTION_EXECUTED") {
    if (message?.type === "email") {
      const recipients = message.content.to?.join(", ");
      return recipients
        ? [verb("emailed"), object(recipients)]
        : [verb("sent an email")];
    }
    if (message?.type === "sms") {
      return message.content.to
        ? [verb("texted"), object(message.content.to)]
        : [verb("sent a text")];
    }
    return [verb("ran"), object(event.eventMetadata.actionName)];
  }
  if (event.eventType === "AGENT_TRIGGER_EXECUTED") {
    const { triggerId, triggerName } = event.eventMetadata;
    if (message?.type === "email" && isReceiveEmailTrigger(triggerId)) {
      return receivedParts("an email", message.content.from);
    }
    if (message?.type === "sms" && isReceiveSmsTrigger(triggerId)) {
      return receivedParts("a text", message.content.from);
    }
    return [verb("was triggered by"), object(triggerName)];
  }
  if (message?.type === "email") {
    return receivedParts("an email", message.content.from);
  }
  if (message?.type === "sms") {
    return receivedParts("a text", message.content.from);
  }
  if (event.actorType === ACTIVITY_EVENT_ACTOR_TYPE.USER) {
    return [verb("messaged"), person(names.agentName)];
  }
  const { messageLabel } = event.eventMetadata;
  return messageLabel
    ? [verb("received"), object(messageLabel)]
    : [verb("received a message")];
}

function receivedParts(
  kind: string,
  from: string | undefined,
): ActivitySentencePart[] {
  return from
    ? [verb(`received ${kind} from`), object(from)]
    : [verb(`received ${kind}`)];
}

// The message's text on one line, for the muted preview under the sentence.
function previewOf(message: ActivityMessage): string | null {
  const text =
    message.type === "message" ? message.message : message.content.body;
  const oneLine = stripHtmlComments(text ?? "")
    .replace(/\s+/g, " ")
    .trim();
  return oneLine.length > 0 ? oneLine : null;
}

/**
 * Removes HTML comments, repeating until none remain so nested fragments can't reassemble one.
 * @param text - The raw message text
 * @returns The text with every HTML comment removed
 */
function stripHtmlComments(text: string): string {
  let stripped = text;
  let previous;
  do {
    previous = stripped;
    stripped = stripped.replace(/<!--[\s\S]*?-->/g, "");
  } while (stripped !== previous);
  return stripped;
}

function createdChip(
  metadata: OpportunityCreatedMetadata,
  names: ActivityNames,
): ActivitySentencePart {
  const details: ItemHistoryDetail[] = [valueLine("Name", metadata.name)];
  if (metadata.stageId) {
    details.push(
      valueLine(
        "Stage",
        nameOf(names.stageNames, metadata.stageId, "Unknown stage"),
      ),
    );
  } else {
    details.push(
      valueLine("Status", OPPORTUNITY_STATUS_DISPLAY_NAMES[metadata.status]),
    );
  }
  details.push(valueLine("Amount", formatCurrency(metadata.amount)));
  if (metadata.probability !== undefined) {
    details.push(valueLine("Probability", `${metadata.probability}%`));
  }
  if (metadata.salesRepUserId) {
    details.push(
      valueLine(
        "Sales rep",
        nameOf(names.userNames, metadata.salesRepUserId, "Unknown user"),
      ),
    );
  }
  return chip(`${details.length} fields`, details);
}

function quoteFieldLines(
  m: QuoteUpdatedMetadata,
  names: ActivityNames,
): ActivityChangeLine[] {
  const lines: ActivityChangeLine[] = [];
  if (m.oldStatus && m.newStatus) {
    lines.push(
      changedLine(
        "status",
        "Status",
        QUOTE_STATUS_DISPLAY_NAMES[m.oldStatus],
        QUOTE_STATUS_DISPLAY_NAMES[m.newStatus],
      ),
    );
  }
  if (m.newName !== undefined) {
    lines.push(
      changedLine("name", "Name", m.oldName || NONE, m.newName || NONE),
    );
  }
  if (m.newCustomerCompanyId !== undefined) {
    lines.push(
      changedLine(
        "customer",
        "Customer",
        nameOf(names.companyNames, m.oldCustomerCompanyId, "Unknown company"),
        nameOf(names.companyNames, m.newCustomerCompanyId, "Unknown company"),
      ),
    );
  }
  if (m.newSalesRepUserId !== undefined) {
    lines.push(salesRepLine(m.oldSalesRepUserId, m.newSalesRepUserId, names));
  }
  if (m.newPersonIds !== undefined) {
    lines.push(peopleLine(m.oldPersonIds, m.newPersonIds, names));
  }
  if (m.newLocationId !== undefined) {
    lines.push(locationLine(m.oldLocationId, m.newLocationId, names));
  }
  if (m.newQuoteDate !== undefined) {
    lines.push(
      changedLine(
        "quoteDate",
        "Quote date",
        formatDateOnly(m.oldQuoteDate) ?? NONE,
        formatDateOnly(m.newQuoteDate) ?? NONE,
      ),
    );
  }
  if (m.newExpirationDate !== undefined) {
    lines.push(
      changedLine(
        "expirationDate",
        "Expiration date",
        formatDateOnly(m.oldExpirationDate) ?? NONE,
        formatDateOnly(m.newExpirationDate) ?? NONE,
      ),
    );
  }
  if (m.newPaymentTerms !== undefined) {
    lines.push(
      changedLine(
        "paymentTerms",
        "Payment terms",
        m.oldPaymentTerms || NONE,
        m.newPaymentTerms || NONE,
      ),
    );
  }
  if (m.newDeliveryTerms !== undefined) {
    lines.push(
      changedLine(
        "deliveryTerms",
        "Delivery terms",
        m.oldDeliveryTerms || NONE,
        m.newDeliveryTerms || NONE,
      ),
    );
  }
  if (m.oldShowTotals !== undefined && m.newShowTotals !== undefined) {
    lines.push(
      changedLine(
        "showTotals",
        "Show totals",
        yesNo(m.oldShowTotals),
        yesNo(m.newShowTotals),
      ),
    );
  }
  if (m.newSubtotal !== undefined) {
    lines.push(
      changedLine(
        "subtotal",
        "Subtotal",
        moneyOrNone(m.oldSubtotal),
        moneyOrNone(m.newSubtotal),
      ),
    );
  }
  if (m.newTotal !== undefined) {
    lines.push(
      changedLine(
        "total",
        "Total",
        moneyOrNone(m.oldTotal),
        moneyOrNone(m.newTotal),
      ),
    );
  }
  return lines;
}

// Edits the write flagged without recording their values.
function quoteFlagLines(m: QuoteUpdatedMetadata): ItemHistoryDetail[] {
  const details: ItemHistoryDetail[] = [];
  if (m.termsOfServiceChanged) {
    details.push(valueLine("Terms of service", "Edited"));
  }
  if (m.discountsChanged) {
    details.push(valueLine("Discounts", "Edited"));
  }
  if (m.lineItemsChanged && !m.lineItemChanges?.length) {
    details.push(
      valueLine(
        "Line items",
        `${m.itemsAdded ?? 0} added, ${m.itemsRemoved ?? 0} removed`,
      ),
    );
  }
  return details;
}

function lineItemDetails(change: LineItemChange): ItemHistoryDetail[] {
  const name = change.itemName || change.description || "Untitled";
  const facts = change.lineType.toLowerCase();
  if (change.changeType === "ADDED" || change.changeType === "REMOVED") {
    return [
      {
        type: change.changeType === "ADDED" ? "added" : "removed",
        key: change.lineId,
        name,
        facts,
        target: null,
      },
    ];
  }
  const edits: ActivityChangeLine[] = [];
  if (
    change.oldDescription !== undefined &&
    change.newDescription !== undefined
  ) {
    edits.push({
      ...changedLine(
        `${change.lineId}.description`,
        name,
        change.oldDescription || "Empty",
        change.newDescription || "Empty",
      ),
      field: "Description",
    });
  }
  if (change.oldItemId !== undefined && change.newItemId !== undefined) {
    edits.push({
      ...changedLine(
        `${change.lineId}.item`,
        name,
        change.oldItemName || change.oldItemId || NONE,
        change.newItemName || change.newItemId || NONE,
      ),
      field: "Item",
    });
  }
  return edits;
}

function salesRepLine(
  from: string | null | undefined,
  to: string | null | undefined,
  names: ActivityNames,
): ActivityChangeLine {
  return changedLine(
    "salesRep",
    "Sales rep",
    nameOf(names.userNames, from, "Unknown user"),
    nameOf(names.userNames, to, "Unknown user"),
  );
}

function peopleLine(
  from: readonly string[] | undefined,
  to: readonly string[] | undefined,
  names: ActivityNames,
): ActivityChangeLine {
  return changedLine(
    "people",
    "People",
    namesOf(names.personNames, from, "Unknown person"),
    namesOf(names.personNames, to, "Unknown person"),
  );
}

function locationLine(
  from: string | null | undefined,
  to: string | null | undefined,
  names: ActivityNames,
): ActivityChangeLine {
  return changedLine(
    "location",
    "Location",
    nameOf(names.locationNames, from, "Unknown location"),
    nameOf(names.locationNames, to, "Unknown location"),
  );
}

// One field inline when its values fit; a chip holding the line when they do not.
function singleChange(
  verbText: string,
  line: ActivityChangeLine,
  trailing: ActivitySentencePart[],
): ActivitySentence {
  if (line.from.length + line.to.length <= INLINE_CHANGE_MAX_CHARS) {
    return {
      ...sentence([verb(verbText), object(line.name), ...trailing]),
      change: line,
    };
  }
  return sentence([verb(verbText), chip(line.name, [line]), ...trailing]);
}

function taskSentence(
  verbText: string,
  metadata: { actionName: string; entityId?: string; entityType?: string },
  names: ActivityNames,
): ActivitySentence {
  return sentence([
    verb(verbText),
    object(metadata.actionName),
    ...taskQuoteParts(metadata.entityId, metadata.entityType, names),
  ]);
}

function taskQuoteParts(
  entityId: string | undefined,
  entityType: string | undefined,
  names: ActivityNames,
): ActivitySentencePart[] {
  if (!entityId || entityType !== TASK_METADATA_ENTITY_TYPE.QUOTE) {
    return [];
  }
  const displayNumber = names.quoteDisplayNumbers.get(entityId);
  if (!displayNumber) {
    return [];
  }
  return [
    verb("for"),
    { type: "quote", quoteId: entityId, label: `Quote #${displayNumber}` },
  ];
}

function quotePart(
  metadata: { quoteId: string; quoteNumber: number },
  names: ActivityNames,
): ActivitySentencePart {
  return {
    type: "quote",
    quoteId: metadata.quoteId,
    label: quoteLabel(metadata.quoteNumber, names),
  };
}

function quoteLabel(quoteNumber: number, names: ActivityNames): string {
  const number = formatQuoteNumber(
    quoteNumber,
    names.quoteNumberPadWidth,
    names.quoteNumberPrefix,
  );
  return `Quote #${number}`;
}

function userActor(member: ItemHistoryPerson): ItemHistoryActor {
  return { ...member, icon: User, tone: "user", context: null, behalf: null };
}

function faceless(
  name: string,
  icon: ItemHistoryActor["icon"],
): Omit<ItemHistoryActor, "tone"> {
  return {
    name,
    icon,
    initials: null,
    imageUrl: null,
    context: null,
    behalf: null,
  };
}

function nameOf(
  namesById: ReadonlyMap<string, string>,
  id: string | null | undefined,
  fallback: string,
): string {
  if (!id) {
    return NONE;
  }
  return namesById.get(id) ?? fallback;
}

function namesOf(
  namesById: ReadonlyMap<string, string>,
  ids: readonly string[] | undefined,
  fallback: string,
): string {
  if (!ids || ids.length === 0) {
    return NONE;
  }
  return ids.map((id) => namesById.get(id) ?? fallback).join(", ");
}

function moneyOrNone(amount: string | null | undefined): string {
  return amount ? formatCurrency(amount) : NONE;
}

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function changedLine(
  key: string,
  name: string,
  from: string,
  to: string,
): ActivityChangeLine {
  return { type: "changed", key, name, field: null, from, to, target: null };
}

function valueLine(label: string, value: string): ItemHistoryDetail {
  return { type: "value", key: label, label, value };
}

function sentence(parts: ActivitySentencePart[]): ActivitySentence {
  return { parts, change: null, context: null, preview: null };
}

function verb(text: string): ActivitySentencePart {
  return { type: "verb", text };
}

function object(text: string): ActivitySentencePart {
  return { type: "object", text };
}

function person(text: string): ActivitySentencePart {
  return { type: "person", text };
}

function chip(
  label: string,
  details: ItemHistoryDetail[],
): ActivitySentencePart {
  return { type: "chip", label, details };
}
